// Радіальний гейдж кабінету — той самий, що в EMS Box («ФЕС · сонячна
// генерація»): дуга 270°, градуювання з 40 рисок (кожна п'ята мажорна),
// мітки шкали біля кінців дуги, число по центру шрифтом font-display.
//
// Винесено сюди, бо в pages/ems/page.tsx це локальна функція всередині чужого
// файлу. Параметри й геометрія збігаються один в один, тож коли власник /ems
// перейде на цей компонент, вигляд не зміниться.
//
// Колір дуги передає викликач: за §3 поточний стан сам по собі не фарбується,
// тож за замовчуванням — нейтральний --text.

interface Props {
  value: number | null;
  max: number;
  /** Одиниця під числом, напр. «кВт AC». */
  unit: string;
  /** Підпис ПІД кільцем окремим рядком, напр. «Загальна потужність AC · 1.7%». */
  sub?: string;
  color?: string;
  size?: number;
  /** Підпис лівої та правої мітки шкали. За замовчуванням 0 і max/1000. */
  scaleMin?: string;
  scaleMax?: string;
  /** Множник розміру центрального числа (за замовчуванням 0.23 * size). Менше
      значення - для великих чисел (5+ цифр), щоб не тулилось до країв кільця. */
  valueScale?: number;
}

const fmt0 = (v: number) => v.toLocaleString('uk-UA', { maximumFractionDigits: 0 });

export default function RadialGauge({
  value, max, unit, sub, color = 'var(--text)', size = 216, scaleMin, scaleMax, valueScale = 0.23,
}: Props) {
  const pct = value == null || max <= 0 ? 0 : Math.max(0, Math.min(1, value / max));
  const c = size / 2;
  const r = size / 2 - 26;
  const circ = 2 * Math.PI * r;
  const arc = circ * (270 / 360);

  const N = 40;
  const ticks = [];
  for (let i = 0; i <= N; i++) {
    const f = i / N;
    const major = i % 5 === 0;
    const ang = ((135 + 270 * f) * Math.PI) / 180;
    const ro = r + 9;
    const ri = r + (major ? 18 : 13);
    const lit = f <= pct + 1e-6;
    ticks.push(
      <line
        key={i}
        x1={c + ro * Math.cos(ang)} y1={c + ro * Math.sin(ang)}
        x2={c + ri * Math.cos(ang)} y2={c + ri * Math.sin(ang)}
        stroke={lit ? color : 'var(--line-strong)'}
        strokeWidth={major ? 2.4 : 1.2}
        strokeLinecap="round"
        opacity={lit ? 0.95 : 0.5}
        style={{ transition: 'stroke .5s' }}
      />,
    );
  }

  const endLabel = (f: number, txt: string) => {
    const ang = ((135 + 270 * f) * Math.PI) / 180;
    const rl = r + 30;
    return (
      <text
        x={c + rl * Math.cos(ang)} y={c + rl * Math.sin(ang)}
        textAnchor="middle" dominantBaseline="middle"
        fontSize={11} fill="var(--text-dim)"
      >
        {txt}
      </text>
    );
  };

  // Кільце й підпис — дві окремі області в потоці, а не один шар з absolute.
  // Через спільний шар підпис лягав на нижню дугу й на риски градуювання.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block', overflow: 'visible' }}>
        <g transform={`rotate(135 ${c} ${c})`}>
          <circle cx={c} cy={c} r={r} fill="none" stroke="var(--line-strong)" strokeWidth={9}
            strokeDasharray={`${arc} ${circ}`} strokeLinecap="round" opacity={0.4} />
          <circle cx={c} cy={c} r={r} fill="none" stroke={color} strokeWidth={9}
            strokeDasharray={`${arc * pct} ${circ}`} strokeLinecap="round"
            style={{ transition: 'stroke-dasharray .7s cubic-bezier(.4,0,.2,1)', filter: `drop-shadow(0 0 7px ${color})` }} />
        </g>
        {ticks}
        {endLabel(0, scaleMin ?? '0')}
        {endLabel(1, scaleMax ?? `${max / 1000}`)}
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span className="font-display" style={{ fontSize: size * valueScale, fontWeight: 600, lineHeight: 1, color: 'var(--text)' }}>
          {value == null ? '—' : fmt0(value)}
        </span>
        <span style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 2 }}>{unit}</span>
      </div>
    </div>
    {/* Підпис у власній області під кільцем: власний рядок потоку, тож він
        фізично не може перетнути дугу за жодного розміру чи довжини тексту. */}
    {sub && (
      <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--text-muted)', textAlign: 'center', maxWidth: size + 40 }}>
        {sub}
      </div>
    )}
    </div>
  );
}
